import { BrowserRouter, Route, Routes, useNavigate } from 'react-router';
import { useEffect, lazy, Suspense } from 'react';
import Header from '@/components/Header';
import ProtectedRoute from '@/components/ProtectedRoute';
import SuperuserRoute from '@/components/SuperuserRoute';
import { ToastManager } from '@/components/ToastManager';
import { useUserStore } from '@/stores/user';
import { ProgressSpinner } from 'primereact/progressspinner';
import { consumePostLoginRedirect } from '@/utils/lessonLinks';

import './App.scss';
import '@livekit/components-styles';

const CreateMeeting = lazy(() => import('@/pages/CreateMeeting'));
const Admin = lazy(() => import('@/pages/Admin'));
const Landing = lazy(() => import('@/pages/Landing'));
const Login = lazy(() => import('@/pages/Login'));
const Register = lazy(() => import('@/pages/Register'));
const ForgotPassword = lazy(() => import('@/pages/ForgotPassword'));
const ResetPassword = lazy(() => import('@/pages/ResetPassword'));
const VerifyEmail = lazy(() => import('@/pages/VerifyEmail'));
const Profile = lazy(() => import('@/pages/Profile'));
const MeetingRoute = lazy(() => import('@/pages/Meeting/MeetingRoute'));
const LessonEntry = lazy(() => import('@/pages/LessonEntry'));
const GroupsList = lazy(() => import('@/pages/Groups'));
const History = lazy(() => import('@/pages/History'));
const GroupPage = lazy(() => import('@/pages/GroupPage'));
const JoinGroup = lazy(() => import('@/pages/JoinGroup'));
const VerifyAssistantInviteToken = lazy(() => import('@/pages/VerifyAssistantInviteToken'));
const MeetingMaterials = lazy(() => import('./pages/MeetingMaterials'));
const MeetingBoardViewer = lazy(() => import('@/pages/MeetingBoardViewer'));
const TgAiChat = lazy(() => import('@/pages/TgAiChat'));
const StudentGuide = lazy(() => import('@/pages/StudentGuide'));
const TeacherGuide = lazy(() => import('@/pages/TeacherGuide'));
const NotFound = lazy(() => import('@/pages/NotFound'));

const LoadingFallback = () => (
  <div
    style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}
  >
    <ProgressSpinner style={{ width: '40px', height: '40px' }} strokeWidth="4" />
  </div>
);

// OAuth providers return to the site root. Once the session is picked up,
// continue to the lesson or invite link the login was started from (if any).
function PostLoginRedirect() {
  const navigate = useNavigate();
  const hydrated = useUserStore((s) => s.hydrated);
  const isAuthenticated = useUserStore((s) => s.isAuthenticated);

  useEffect(() => {
    if (!hydrated || !isAuthenticated) return;
    const next = consumePostLoginRedirect();
    if (next) {
      navigate(next, { replace: true });
    }
  }, [hydrated, isAuthenticated, navigate]);

  return null;
}

function App() {
  // Probe the auth cookie once on load (also picks up the session after an
  // OAuth provider redirects back into the SPA).
  useEffect(() => {
    useUserStore.getState().hydrate();
  }, []);

  return (
    <>
      <ToastManager />
      <div id="app">
        <BrowserRouter>
          <Header withBorder />
          <PostLoginRedirect />
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              <Route path="/" element={<CreateMeeting />} />
              <Route path="/landing" element={<Landing />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                }
              />
              <Route path="/meeting/:meetingUid" element={<MeetingRoute />} />
              <Route path="/lesson/:code" element={<LessonEntry />} />
              <Route
                path="/groups"
                element={
                  <ProtectedRoute>
                    <GroupsList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/history"
                element={
                  <ProtectedRoute>
                    <History />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin"
                element={
                  <SuperuserRoute>
                    <Admin />
                  </SuperuserRoute>
                }
              />
              <Route
                path="/classrooms/:classroomId"
                element={
                  <ProtectedRoute>
                    <GroupPage />
                  </ProtectedRoute>
                }
              />
              <Route path="/join/:code" element={<JoinGroup />} />
              <Route
                path="/meeting/assistant/:assistantInviteToken"
                element={<VerifyAssistantInviteToken />}
              />
              <Route path="/meeting/:meetingUid/materials" element={<MeetingMaterials />} />
              <Route
                path="/meeting/:meetingUid/materials/boards/:roomId"
                element={<MeetingBoardViewer />}
              />
              <Route path="/ai/answers/:answerUid" element={<TgAiChat />} />
              <Route path="/student-guide" element={<StudentGuide />} />
              <Route path="/teacher-guide" element={<TeacherGuide />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </div>
    </>
  );
}

export default App;
